import classNames from 'classnames';
import PropTypes from 'prop-types';
import { useLayoutEffect, useRef, useState } from 'react';
import { useHoverCard } from './useHoverCard';
const POSITION = {
  ABOVE: 'above',
  BELOW: 'below'
};

/* HoverCard Container */
const containerCss = "cysuv6z";

/* Hover Card Wrapper - handles positioning (below by default) */
const cardWrapperCss = "c10olu2m";
const cardWrapperAboveCss = "cfdxna4";

/* Main Card Styling */
const cardCss = "ck0r0uu";

/* Header Slot */
const headerCss = "h18bowje";

/* Body Slot */
const bodyCss = "b12q228e";

/* Footer Slot */
const footerCss = "fxvu6xf";

/**
 * A generic hover card component that displays content when hovering over a trigger element.
 *
 * This component provides:
 * - Hover behavior management (show/hide with delay)
 * - Slot-based composition (header, body, footer) or children
 * - Consistent styling and positioning
 * - Auto-positioning: renders below if there's room, otherwise above
 *
 * @param {Object} props - Component props
 * @param {React.ReactElement} props.trigger - Required: Element that triggers the hover card
 * @param {React.ReactNode} [props.header] - Optional: Header slot content
 * @param {React.ReactNode} [props.body] - Optional: Body slot content
 * @param {React.ReactNode} [props.footer] - Optional: Footer slot content
 * @param {React.ReactNode} [props.children] - Alternative to slots - raw content for the card
 * @param {string} [props.className] - Additional CSS class for the container
 * @param {string} [props.cardClassName] - Additional CSS class for the card
 * @param {string} [props.wrapperClassName] - Additional CSS class for the card wrapper (use for custom width)
 *
 * @returns {JSX.Element} The hover card component
 *
 * @example
 * // Pattern 1: Slot-based (recommended for complex layouts)
 * <HoverCard
 *   trigger={<Button>Hover me</Button>}
 *   header={<CardHeader title="Title" />}
 *   body={<CardContent />}
 *   footer={<CardFooter onAction={handleAction} />}
 * />
 *
 * @example
 * // Pattern 2: Children (for simpler use cases)
 * <HoverCard trigger={<Button>Hover me</Button>}>
 *   <CustomContent />
 * </HoverCard>
 */
export const HoverCard = ({
  trigger,
  header = null,
  body = null,
  footer = null,
  children = null,
  className = '',
  cardClassName = '',
  wrapperClassName = ''
}) => {
  const {
    isVisible,
    triggerProps
  } = useHoverCard();
  const containerRef = useRef(null);
  const cardWrapperRef = useRef(null);
  const [position, setPosition] = useState(POSITION.BELOW);

  // Calculate position when card becomes visible
  useLayoutEffect(() => {
    if (!isVisible || !containerRef.current || !cardWrapperRef.current) {
      return;
    }
    const containerRect = containerRef.current.getBoundingClientRect();
    const cardRect = cardWrapperRef.current.getBoundingClientRect();
    const viewportHeight = window.innerHeight;

    // Calculate space below and above the trigger
    const spaceBelow = viewportHeight - containerRect.bottom;
    const cardHeight = cardRect.height;

    // If not enough space below, position above
    if (spaceBelow < cardHeight && containerRect.top > cardHeight) {
      setPosition(POSITION.ABOVE);
    } else {
      setPosition(POSITION.BELOW);
    }
  }, [isVisible]);

  // Determine if we should use slots or children
  const hasSlots = header || body || footer;
  const cardContent = hasSlots ? <>
      {header && <div className={headerCss}>{header}</div>}
      {body && <div className={bodyCss}>{body}</div>}
      {footer && <div className={footerCss}>{footer}</div>}
    </> : children;
  const isAbove = position === POSITION.ABOVE;
  return <div ref={containerRef} className={classNames(containerCss, className)} {...triggerProps}>
      {trigger}
      {isVisible && <div ref={cardWrapperRef} className={classNames(cardWrapperCss, {
      [cardWrapperAboveCss]: isAbove
    }, wrapperClassName)}>
          <div className={classNames(cardCss, cardClassName)}>
            {cardContent}
          </div>
        </div>}
    </div>;
};
HoverCard.propTypes = {
  trigger: PropTypes.element.isRequired,
  header: PropTypes.node,
  body: PropTypes.node,
  footer: PropTypes.node,
  children: PropTypes.node,
  className: PropTypes.string,
  cardClassName: PropTypes.string,
  wrapperClassName: PropTypes.string
};

require("./HoverCard.wyw-in-js.css!=!../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./HoverCard.js");